// src/app/pages/definition/CashRegister/CashRegisterPage.tsx
import React, {FC, useCallback, useEffect, useState} from 'react'
import {Form, Formik, FormikProps} from 'formik'
import {
  ICashRegisterModel,
  initialCashRegister,
  cashRegisterValidation,
} from './CashRegisterModel'
import {
  allCashRegister,
  createCashRegister,
  deleteCashRegister,
  updateCashRegister,
} from './Api'
import {ConfirmModal} from '../../../../shared/components/ConfirmModal'
import {ToastContainer} from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import FormTabHeader, {TabOption} from '../../../../shared/components/FormTabHeader'
import {showErrorToast, showSuccessToast} from '../../../../shared/components/ToastUtils'
import TextInput from '../../../../shared/components/TextInput'
import SelectInput from '../../../../shared/components/SelectInput'
import { CashRegisterTable, CashRegisterRow } from './CashRegisterTable'

// 🔁 Ortak lookuplar
import { currencies, branches, cashReqisters } from '../../../../shared/models/SharedApiLookups'

const CashRegisterPage: FC = () => {
  const [tab, setTab] = useState<string | 'form'>('form')
  const [cashRegisters, setCashRegisters] = useState<ICashRegisterModel[]>([])
  const [loading, setLoading] = useState(false)
  const [selectedId, setSelectedId] = useState<string | null | undefined>(null)
  const [showModal, setShowModal] = useState(false)
  const [currentPage, setCurrentPage] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [sortBy, setSortBy] = useState<string>('createdDate')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  const [error, setError] = useState<string | null>(null)

  // 🔁 Select options
  const [currencyOptions, setCurrencyOptions] = useState<{value: string; label: string}[]>([])
  const [branchOptions, setBranchOptions] = useState<{value: string; label: string}[]>([])
  const [kasaKoduOptions, setKasaKoduOptions] = useState<{value: string; label: string}[]>([])

  const tabOptions: TabOption<'form' | 'list'>[] = [
    {key: 'form', title: 'Kasa Tanımlama', icon: 'bi bi-cash'},
    {key: 'list', title: 'Kasa Listesi', icon: 'bi bi-list'},
  ]

  useEffect(() => {
    // 🔁 Tüm lookupları tek seferde çek
    Promise.all([currencies(), branches(), cashReqisters()])
      .then(([cr, br, ks]) => {
        setCurrencyOptions(cr)
        setBranchOptions(br)
        setKasaKoduOptions(ks)
      })
      .catch(() => {
        // sessiz düş
      })
  }, [])

  const fetchCashRegisters = useCallback(
    async (page: number = 0, sort?: string, order?: 'asc' | 'desc') => {
      setLoading(true)
      setError(null)
      try {
        const response = await allCashRegister(page, 5, sort ?? sortBy, order ?? sortOrder)
        if (response.data.hasError) throw new Error(response.data.message || 'Failed to fetch cash registers')
        setCashRegisters(response.data.payload.content)
        setTotalPages(response.data.payload.totalPages)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error occurred')
      } finally {
        setLoading(false)
      }
    },
    [sortBy, sortOrder]
  )

  useEffect(() => {
    if (tab === 'list') fetchCashRegisters(0)
  }, [tab, fetchCashRegisters])

  const handleSave = async (values: ICashRegisterModel, resetForm: () => void) => {
    setLoading(true)
    try {
      if (selectedId) {
        await updateCashRegister({...values, id: Number(selectedId)})
        showSuccessToast('Kasa başarıyla güncellendi')
      } else {
        await createCashRegister(values)
        showSuccessToast('Kasa başarıyla oluşturuldu')
      }
      setCurrentPage(0)
      await fetchCashRegisters(0)
      resetForm()
      setSelectedId(null)
      setTab('list')
    } catch {
      showErrorToast('Bir hata oluştu')
    } finally {
      setLoading(false)
    }
  }

  const toTableRows = (data: ICashRegisterModel[]): CashRegisterRow[] =>
    data.map((r) => ({
      ...r,
      id: r.id != null ? String(r.id) : undefined,
      createdAt: r.createdAt ? new Date(r.createdAt) : undefined,
      updatedAt: r.updatedAt ? new Date(r.updatedAt) : undefined,
    }))

  const handleEdit = useCallback(
    (row: CashRegisterRow, formik: FormikProps<ICashRegisterModel>) => {
      const original: ICashRegisterModel = {
        ...row,
        id: row.id != null ? Number(row.id) : undefined,
        createdAt: row.createdAt,
        updatedAt: row.updatedAt,
      }
      formik.setValues(original)
      setSelectedId(row.id ?? null)
      setTab('form')
    },
    []
  )

  const handleDeleteClick = useCallback((row: CashRegisterRow) => {
    setSelectedId(row.id ?? null)
    setShowModal(true)
  }, [])

  const handleDelete = async () => {
    if (selectedId) {
      setLoading(true)
      try {
        await deleteCashRegister(selectedId)
        showSuccessToast('Kasa başarıyla silindi')
        await fetchCashRegisters(currentPage)
      } catch (err) {
        console.error(err)
        showErrorToast('Silinirken bir hata oluştu')
      } finally {
        setLoading(false)
        setShowModal(false)
        setSelectedId(null)
      }
    }
  }

  const handlePageChange = useCallback(
    (newPage: number) => {
      if (newPage >= 0 && newPage < totalPages) {
        setCurrentPage(newPage)
        fetchCashRegisters(newPage, sortBy, sortOrder)
      }
    },
    [totalPages, sortBy, sortOrder, fetchCashRegisters]
  )

  const handleSort = useCallback(
    (column: string, order: 'asc' | 'desc') => {
      setSortBy(column)
      setSortOrder(order)
      fetchCashRegisters(currentPage, column, order)
    },
    [currentPage, fetchCashRegisters]
  )

  return (
    <>
      <Formik
        initialValues={initialCashRegister}
        validationSchema={cashRegisterValidation}
        enableReinitialize={true}
        onSubmit={(values, {resetForm}) => handleSave(values, resetForm)}
      >
        {(formik) => {
          const handleReset = () => {
            formik.resetForm()
            setSelectedId(null)
          }

          return (
            <>
              <FormTabHeader
                formId='cashRegisterForm'
                formikSubmit={formik.handleSubmit}
                formikReset={handleReset}
                loading={loading}
                tab={tab}
                setTab={setTab}
                setSelectedId={setSelectedId}
                tabs={[{key:'form', title:'Kasa Tanımlama', icon:'bi bi-cash'},{key:'list', title:'Kasa Listesi', icon:'bi bi-list'}]}
                onExportExcel={() => console.log('Excel export')}
              />

              {tab === 'form' && (
                <div className='card mb-5'>
                  <Form id='cashRegisterForm' className='form card-body'>
                    {/* 1) CARI ARA + Makbuz No */}
                    <div className='row mb-6'>
                      <div className='col-md-6'>
                        <div className='d-flex align-items-center gap-3'>
                          <label className='form-label mb-0 fw-semibold' style={{minWidth: 90}}>
                            Cari Ara
                          </label>
                          <input
                            type='text'
                            name='cariAra'
                            className='form-control'
                            value={formik.values.cariAra ?? ''}
                            onChange={formik.handleChange}
                          />
                        </div>
                      </div>
                      <TextInput name='receiptNo' label='Makbuz Numarası' wrapperClassName='col-md-6' />
                    </div>

                    {/* 2) Cari Bilgi + Kasa Kodu (lookup) */}
                    <div className='row mb-6'>
                      <div className='col-md-6'>
                        <div className='row g-3 align-items-start'>
                          <div className='col-sm-3 d-flex align-items-center'>
                            <span className='form-label mb-0 fw-semibold'>Cari Bilgi</span>
                          </div>
                          <div className='col-sm-9'>
                            <input
                              type='text'
                              name='name'
                              className='form-control mb-3'
                              placeholder='Ad'
                              value={formik.values.name}
                              onChange={formik.handleChange}
                            />
                            <input
                              type='text'
                              name='lastName'
                              className='form-control'
                              placeholder='Soyad'
                              value={formik.values.lastName}
                              onChange={formik.handleChange}
                            />
                          </div>
                        </div>
                      </div>

                      {/* ✅ Kasa Kodu artık SharedApiLookups > cashReqisters() */}
                      <SelectInput
                        name='kasaKodu'
                        label='Kasa Kodu'
                        wrapperClassName='col-md-6'
                        options={kasaKoduOptions}
                      />
                    </div>

                    {/* 3) Tarih (kısa) | Kasa Kur */}
                    <div className='row mb-6'>
                      <TextInput name='tarih' type='date' label='Tarih' wrapperClassName='col-md-3' />
                      <div className='col-md-3' />
                      <TextInput name='kasaKur' type='number' label='Kasa Kur' wrapperClassName='col-md-6' />
                    </div>

                    {/* 4) Kasa Tutar / PB | Cari Kur */}
                    <div className='row mb-6'>
                      <TextInput name='kasaTutar' type='number' label='Kasa Tutar' wrapperClassName='col-md-3' />
                      <SelectInput
                        name='kasaTutarCurrency'
                        label='Para Birimi'
                        wrapperClassName='col-md-3'
                        options={currencyOptions}
                      />
                      <TextInput name='cariKur' type='number' label='Cari Kur' wrapperClassName='col-md-6' />
                    </div>

                    {/* 5) Cari Tutar / PB | İşlem Şube */}
                    <div className='row mb-6'>
                      <TextInput name='cariTutar' type='number' label='Cari Tutar' wrapperClassName='col-md-3' />
                      <SelectInput
                        name='cariTutarCurrency'
                        label='Para Birimi'
                        wrapperClassName='col-md-3'
                        options={currencyOptions}
                      />
                      <SelectInput
                        name='islemSube'
                        label='İşlem Şube'
                        wrapperClassName='col-md-6'
                        options={branchOptions}
                      />
                    </div>

                    {/* 6) İşlemi Yapan | Açıklama */}
                    <div className='row mb-6'>
                      <TextInput name='islemiYapan' label='İşlemi Yapan' placeholder='Örn: BURAK KAYHAN' wrapperClassName='col-md-6' />
                      <TextInput name='aciklama' label='Açıklama' wrapperClassName='col-md-6' />
                    </div>
                  </Form>
                </div>
              )}

              {tab === 'list' && (
                <div className='card'>
                  <div className='card-body pt-0'>
                    <CashRegisterTable
                      data={toTableRows(cashRegisters)}
                      currentPage={currentPage}
                      totalPages={totalPages}
                      onPageChange={handlePageChange}
                      onEdit={(row) => handleEdit(row, formik)}
                      onDelete={handleDeleteClick}
                      sortBy={sortBy}
                      sortOrder={sortOrder}
                      onSort={handleSort}
                      loading={loading}
                    />
                  </div>
                </div>
              )}
            </>
          )
        }}
      </Formik>

      <ConfirmModal
        show={showModal}
        header='Silme Onayı'
        text='Bu kaydı silmek istediğinize emin misiniz?'
        handleClose={() => {
          setShowModal(false)
          setSelectedId(null)
        }}
        applyOnClick={handleDelete}
      />

      <ToastContainer position='top-right' autoClose={5000} newestOnTop pauseOnHover />
    </>
  )
}

export default CashRegisterPage
